<template>
  <div class="lc-performance">
    <!-- Header -->
    <div class="vd-header">
      <div>
        <div class="vd-title">Loss Control Impact</div>
        <div class="vd-subtitle">Reported loss vs. recovered vs. booked — signed loss control jobs</div>
      </div>
    </div>

    <!-- Stat cards -->
    <div class="stat-cards">
      <div class="stat-card">
        <div>
          <div class="stat-label">Loss Recovered</div>
          <div class="stat-date-range">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</div>
          <div class="stat-value green">{{ fmt(summary.recovered) }} MT</div>
        </div>
        <div class="stat-icon stat-icon--green">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
          </svg>
        </div>
      </div>
      <div class="stat-card">
        <div>
          <div class="stat-label">Avg Loss Reduced</div>
          <div class="stat-date-range">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</div>
          <div class="stat-value green">{{ summary.avgReducedPct }}%</div>
        </div>
        <div class="stat-icon stat-icon--green">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
          </svg>
        </div>
      </div>
      <div class="stat-card">
        <div>
          <div class="stat-label">Loss Control Coverage</div>
          <div class="stat-date-range">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</div>
          <div class="stat-value blue">{{ summary.lcJobs.toLocaleString() }} / {{ summary.totalJobs.toLocaleString() }} jobs</div>
        </div>
        <div class="stat-icon stat-icon--blue">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- Monthly chart -->
    <div class="chart-card">
      <div class="chart-card-header">
        <div class="chart-title-nav">
          <span class="chart-card-title">Loss Control Impact (MT)</span>
          <div class="chart-nav">
            <button class="chart-nav-btn" @click="chartOffset += 1; fetchChart()">&#8249;</button>
            <span class="chart-window-label">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</span>
            <button class="chart-nav-btn" :disabled="chartOffset === 0" @click="chartOffset -= 1; fetchChart()">&#8250;</button>
          </div>
        </div>
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-dot" style="background:#ef4444"></span>Final Quantity</span>
          <span class="legend-item"><span class="legend-dot" style="background:#22c55e"></span>Recovered</span>
        </div>
      </div>
      <div class="chart-hint">Click a month on the chart to filter the list below to that month.</div>
      <div class="chart-scroll">
        <div ref="chartDom" style="height: 340px; width: 100%; min-width: 640px;"></div>
      </div>
    </div>

    <!-- Detail list — always shown, follows the chart window; click a month to filter -->
    <div class="chart-card" v-loading="listLoading">
      <div class="detail-head">
        <div class="detail-summary">
          <span>Total Initial Loss: <b>{{ fmt(summaryRows.initialLoss) }} MT</b></span>
          <span>Total Final Loss: <b class="red">{{ fmt(summaryRows.finalLoss) }} MT</b></span>
          <span>Total Recovered: <b class="green">{{ fmt(summaryRows.recovered) }} MT</b></span>
          <span>Avg recovery: <b>{{ summaryRows.avgPct }}%</b></span>
        </div>
      </div>
      <div class="detail-toolbar">
        <span class="detail-month">
          {{ monthFilter ? monthFilterLabel : (chartWindowLabel.start + ' – ' + chartWindowLabel.end) }}
          <el-tag v-if="monthFilter" size="mini" type="info" closable @close="clearMonthFilter" style="margin-left:8px">
            Month: {{ monthFilterLabel }}
          </el-tag>
        </span>
        <el-input
          v-model="search"
          size="small"
          clearable
          suffix-icon="el-icon-search"
          placeholder="Search ship / barge or company"
          style="width: 260px"
          @input="pageNum = 1"
        />
      </div>
      <el-table :data="pagedRows" size="small" max-height="480" style="width:100%" empty-text="No nominations">
        <el-table-column prop="nominationId" label="Nomination" width="120" />
        <el-table-column prop="shipBarge" label="Ship / Barge" min-width="130" />
        <el-table-column prop="company" label="Company" min-width="140" />
        <el-table-column label="Initial Loss (MT)" width="140" align="right">
          <template slot-scope="scope">{{ fmt(scope.row.initialLoss) }}</template>
        </el-table-column>
        <el-table-column label="Recovered (MT)" width="130" align="right">
          <template slot-scope="scope"><span class="green">{{ fmt(scope.row.recovered) }}</span></template>
        </el-table-column>
        <el-table-column label="Recovered %" width="120" align="right">
          <template slot-scope="scope"><span class="green">{{ recoveredPct(scope.row) }}%</span></template>
        </el-table-column>
        <el-table-column label="Final Loss (MT)" width="130" align="right">
          <template slot-scope="scope"><span class="red">{{ fmt(scope.row.finalLoss) }}</span></template>
        </el-table-column>
      </el-table>
      <div class="list-pagination">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[10, 20, 50]"
          :current-page.sync="pageNum"
          :page-size.sync="pageSize"
          :total="filteredRows.length"
          @size-change="pageNum = 1"
        />
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import moment from 'moment';
import { getLossControlPerformance, getLossControlPerformanceDetail } from '@/services/index';

export default {
  name: 'LossControlPerformance',
  data() {
    return {
      chartOffset: 0,
      monthlyData: [],
      rows: [],
      listLoading: false,
      monthFilter: null,
      search: '',
      pageNum: 1,
      pageSize: 10,
      summary: { recovered: 0, avgReducedPct: '0.00', lcJobs: 0, totalJobs: 0 },
    };
  },
  async created() {
    await this.fetchChart();
  },
  mounted() {
    // Use ECharts directly (vue3-echarts is broken under Vue 2 — its mounted throws on Vue 3's
    // `capitalize`, killing event/resize wiring). We own init, setOption, resize and click here.
    this.chart = echarts.init(this.$refs.chartDom);
    this.chart.setOption(this.chartOption);
    this.chart.getZr().on('click', this.onChartClick);
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  watch: {
    chartOption(opt) {
      if (this.chart) this.chart.setOption(opt);
    },
  },
  computed: {
    monthFilterLabel() {
      return this.monthFilter ? moment(this.monthFilter, 'YYYY-MM').format('MMM YYYY') : '';
    },
    // Rows in scope = window rows, narrowed to the clicked month if one is selected.
    scopedRows() {
      if (!this.monthFilter) return this.rows;
      return this.rows.filter(r => r.month === this.monthFilter);
    },
    // Summary reflects the scoped rows (month/window), independent of the text search.
    summaryRows() {
      const init = this.scopedRows.reduce((s, r) => s + (Number(r.initialLoss) || 0), 0);
      const rec = this.scopedRows.reduce((s, r) => s + (Number(r.recovered) || 0), 0);
      const fin = this.scopedRows.reduce((s, r) => s + (Number(r.finalLoss) || 0), 0);
      return {
        initialLoss: init,
        recovered: rec,
        finalLoss: fin,
        avgPct: init > 0 ? (rec / init * 100).toFixed(2) : '0.00',
      };
    },
    filteredRows() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.scopedRows;
      return this.scopedRows.filter(r =>
        String(r.shipBarge ?? '').toLowerCase().includes(q)
        || String(r.company ?? '').toLowerCase().includes(q)
      );
    },
    pagedRows() {
      const start = (this.pageNum - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    chartDateRange() {
      const fmt = 'YYYY-MM-DD HH:mm:ss';
      return {
        startDate: moment().subtract(5 + this.chartOffset, 'months').startOf('month').format(fmt),
        endDate: moment().subtract(this.chartOffset, 'months').endOf('month').format(fmt),
      };
    },
    chartWindowLabel() {
      return {
        start: moment().subtract(5 + this.chartOffset, 'months').startOf('month').format('MMM YYYY'),
        end: moment().subtract(this.chartOffset, 'months').endOf('month').format('MMM YYYY'),
      };
    },
    chartOption() {
      const monthlyData = this.monthlyData;
      const months = monthlyData.map(m => m.monthLabel);
      return {
        grid: { left: 40, right: 20, top: 20, bottom: 10, containLabel: true },
        xAxis: {
          type: 'category', data: months,
          axisLine: { show: false }, axisTick: { show: false },
          axisLabel: { color: '#94a3b8', fontSize: 12 }, splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } },
          axisLabel: { color: '#94a3b8', fontSize: 11, formatter: v => v + ' MT' },
          axisLine: { show: false }, axisTick: { show: false },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter(params) {
            const month = params[0]?.axisValue;
            const md = monthlyData.find(m => m.monthLabel === month);
            if (!md) return month;
            const pct = md.initialLoss > 0 ? (md.recovered / md.initialLoss * 100).toFixed(2) : '0.00';
            return `<div style="min-width:230px;"><b>${month}</b></div>`
              + `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;">`
              + `<span style="width:9px;height:9px;border-radius:2px;background:#ef4444;"></span>`
              + `Final Quantity: <b>${md.finalLoss.toFixed(3)} MT</b></div>`
              + `<div style="border-top:1px solid #e2e8f0;margin:6px 0;"></div>`
              + `<div style="font-size:11px;color:#334155;">Reported <b>${md.initialLoss.toFixed(3)} MT</b> `
              + `&rarr; booked <b>${md.finalLoss.toFixed(3)} MT</b> `
              + `(<b style="color:#16a34a;">${pct}% recovered</b>)</div>`
              + `<div style="font-size:11px;color:#94a3b8;margin-top:2px;">${md.lcJobs} loss control noms</div>`;
          },
        },
        series: [
          {
            name: 'Final Quantity', type: 'bar', stack: 'loss',
            data: monthlyData.map(m => ({
              value: parseFloat(m.finalLoss.toFixed(3)),
              itemStyle: { color: this.dimColor(m.monthKey, '#ef4444', '#fecaca') },
            })),
            itemStyle: { borderRadius: [0, 0, 0, 0] },
          },
          {
            name: 'Recovered', type: 'bar', stack: 'loss',
            data: monthlyData.map(m => ({
              value: parseFloat(m.recovered.toFixed(3)),
              itemStyle: { color: this.dimColor(m.monthKey, '#22c55e', '#bbf7d0') },
            })),
            itemStyle: { borderRadius: [4, 4, 0, 0] },
          },
        ],
      };
    },
  },
  methods: {
    dimColor(monthKey, full, faded) {
      // no month selected → all bars full; otherwise only the selected month stays full
      return !this.monthFilter || this.monthFilter === monthKey ? full : faded;
    },
    fmt(v) {
      const n = Number(v) || 0;
      return n.toFixed(3);
    },
    recoveredPct(row) {
      const init = Number(row.initialLoss) || 0;
      const rec = Number(row.recovered) || 0;
      return init > 0 ? (rec / init * 100).toFixed(2) : '0.00';
    },
    async fetchChart() {
      let res;
      try {
        res = await getLossControlPerformance(this.chartDateRange);
      } catch (e) {
        this.monthlyData = [];
        return;
      }
      const payload = res?.data ?? {};
      const totalJobs = Number(payload.totalJobs) || 0;
      const byMonth = new Map((payload.monthly ?? []).map(x => [x.month, x]));

      const windowMonths = [];
      for (let i = 5; i >= 0; i--) {
        windowMonths.push(moment().subtract(i + this.chartOffset, 'months').format('YYYY-MM'));
      }
      this.monthlyData = windowMonths.map(month => {
        const r = byMonth.get(month);
        return {
          monthKey: month,
          monthLabel: moment(month, 'YYYY-MM').format('MMM YYYY'),
          initialLoss: r ? Number(r.initialLoss) || 0 : 0,
          recovered: r ? Number(r.recovered) || 0 : 0,
          finalLoss: r ? Number(r.finalLoss) || 0 : 0,
          lcJobs: r ? Number(r.lcJobs) || 0 : 0,
        };
      });

      const sumRecovered = this.monthlyData.reduce((s, m) => s + m.recovered, 0);
      const sumInitial = this.monthlyData.reduce((s, m) => s + m.initialLoss, 0);
      const sumJobs = this.monthlyData.reduce((s, m) => s + m.lcJobs, 0);
      this.summary = {
        recovered: sumRecovered,
        avgReducedPct: sumInitial > 0 ? (sumRecovered / sumInitial * 100).toFixed(2) : '0.00',
        lcJobs: sumJobs,
        totalJobs,
      };

      // Window changed (e.g. paged) — reload the list for the new window and drop any month filter.
      this.fetchList();
    },
    async fetchList() {
      this.listLoading = true;
      this.monthFilter = null;
      this.search = '';
      this.pageNum = 1;
      try {
        const res = await getLossControlPerformanceDetail(this.chartDateRange);
        this.rows = res?.data ?? [];
      } catch (e) {
        this.rows = [];
      } finally {
        this.listLoading = false;
      }
    },
    clearMonthFilter() {
      this.monthFilter = null;
      this.pageNum = 1;
    },
    resizeChart() {
      if (this.chart) this.chart.resize();
    },
    onChartClick(event) {
      if (!this.chart) return;
      const point = [event.offsetX, event.offsetY];
      // Ignore clicks outside the plotting area (axis labels, margins).
      if (!this.chart.containPixel('grid', point)) return;
      // Map pixel -> data via the series coordinate system: returns [categoryIndex, value].
      const dataPoint = this.chart.convertFromPixel({ seriesIndex: 0 }, point);
      const i = Array.isArray(dataPoint) ? Math.round(dataPoint[0]) : NaN;
      const md = this.monthlyData[i];
      if (!md) return;
      // Filter the list to the clicked month (client-side over the already-loaded window rows).
      this.monthFilter = md.monthKey;
      this.search = '';
      this.pageNum = 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.lc-performance {
  padding: 16px;
  background: #f1f5f9bb;
  min-height: 100%;
  border-radius: 10px;

  .vd-header { margin-bottom: 20px; }
  .vd-title { font-size: 22px; font-weight: 700; color: #1e293b; }
  .vd-subtitle { font-size: 13px; color: #94a3b8; margin-top: 2px; }

  .stat-cards {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
    @media (max-width: 900px) { grid-template-columns: 1fr; }

    .stat-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .stat-label {
        font-size: 11px; font-weight: 600; color: #94a3b8;
        letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 6px;
      }
      .stat-date-range { font-size: 11px; color: #94a3b8; margin-bottom: 4px; }
      .stat-value {
        font-size: 30px; font-weight: 700; color: #1e293b; line-height: 1.1;
        &.green { color: #16a34a; }
        &.blue { color: #2563eb; }
        &.red { color: #ef4444; }
      }
      .stat-icon {
        width: 52px; height: 52px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        &--blue { background: #2563eb; }
        &--green { background: #16a34a; }
      }
    }
  }

  .chart-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;

    .chart-card-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 6px; flex-wrap: wrap; gap: 8px;
      .chart-title-nav { display: flex; flex-direction: column; gap: 6px; }
      .chart-nav { display: flex; align-items: center; gap: 8px; }
      .chart-nav-btn {
        background: white; border: 1px solid #e2e8f0; border-radius: 6px;
        width: 26px; height: 26px; font-size: 20px; line-height: 1; color: #64748b;
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        padding: 0; transition: all 0.15s;
        &:hover:not(:disabled) { background: #f1f5f9; color: #2563eb; border-color: #cbd5e1; }
        &:disabled { opacity: 0.35; cursor: default; }
      }
      .chart-window-label { font-size: 12px; color: #64748b; font-weight: 500; white-space: nowrap; }
      .chart-card-title { font-size: 16px; font-weight: 600; color: #1e293b; }
      .chart-legend {
        display: flex; flex-wrap: wrap; gap: 10px;
        .legend-item { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #64748b; }
        .legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
      }
    }
    .chart-hint { font-size: 11px; color: #94a3b8; margin-bottom: 8px; }
    .chart-scroll { overflow-x: auto; }
    .list-pagination { margin-top: 12px; text-align: right; }
  }

  .detail-head {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; flex-wrap: wrap; margin-bottom: 12px;
  }
  .detail-summary {
    display: flex; flex-wrap: wrap; gap: 22px; font-size: 13px; color: #475569;
    b { color: #1e293b; }
    b.green { color: #16a34a; }
    b.red { color: #ef4444; }
  }
  .detail-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; flex-wrap: wrap; margin-bottom: 10px;
    .detail-month { font-size: 15px; font-weight: 600; color: #1e293b; }
  }
  .green { color: #16a34a; }
  .red { color: #ef4444; }
}
</style>
